import { Capacitor } from '@capacitor/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';

const isNative = () => Capacitor.isNativePlatform();
let handlersAttached = false;

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message || error.name || 'Error';
  return typeof error === 'string' ? error : JSON.stringify(error);
}

/**
 * Record a non-fatal error to Crashlytics (native) or console (web/dev).
 * Best-effort — never throws. The JS stack + React componentStack go in as a
 * breadcrumb via log(); the message is the exception title.
 */
export function recordError(
  error: unknown,
  info?: { componentStack?: string; context?: string },
): void {
  if (!isNative()) {
    if (import.meta.env.DEV) console.error('[crashReporter]', error, info);
    return;
  }
  try {
    const stack = error instanceof Error && error.stack ? error.stack : '';
    const breadcrumb = [info?.context, stack, info?.componentStack].filter(Boolean).join('\n');
    if (breadcrumb) void FirebaseCrashlytics.log({ message: breadcrumb }).catch(() => undefined);
    void FirebaseCrashlytics.recordException({ message: messageOf(error) }).catch(() => undefined);
  } catch {
    // best-effort: reporting must never crash the app
  }
}

/**
 * TEST-ONLY: force a native crash to verify Crashlytics end-to-end. No-op on
 * web. Reached only from the VITE_CRASH_TEST settings panel, so it never fires
 * for real users. Deliberately not wrapped in try/catch — the crash is the point.
 */
export function forceCrash(message = 'Test crash from Brainday'): void {
  if (!isNative()) {
    if (import.meta.env.DEV)
      console.warn('[crashReporter] forceCrash is native-only — no-op on web');
    return;
  }
  void FirebaseCrashlytics.crash({ message });
}

export function setCrashUser(uid: string | null): void {
  if (!isNative()) return;
  void FirebaseCrashlytics.setUserId({ userId: uid ?? '' }).catch(() => undefined);
}

/** Mount once at app bootstrap (native only): enable collection + global JS handlers. */
export function initCrashReporter(): void {
  if (!isNative() || handlersAttached) return;
  handlersAttached = true;
  void FirebaseCrashlytics.setEnabled({ enabled: true }).catch(() => undefined);
  window.addEventListener('unhandledrejection', (e) => {
    recordError(e.reason, { context: 'unhandledrejection' });
  });
  window.addEventListener('error', (e) => {
    recordError(e.error ?? e.message, { context: 'window.onerror' });
  });
}
